import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { InputSuelto } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useAuditoria } from './api';
import { useBusquedaDemorada } from './piezas';

/**
 * Auditoría — el registro inmutable de lo que se ha hecho en el portal.
 *
 * ## Una sola pantalla donde había dos
 *
 * `page-audit.js` (la pantalla `#audit`) y la pestaña de Administración eran dos
 * tablas distintas del MISMO endpoint, y ninguna de las dos estaba completa:
 *
 *   · `#audit` tenía el filtro por acción y traía 100 filas, pero no enseñaba el
 *     motivo —la columna que explica POR QUÉ se hizo algo— ni el enlace a la
 *     publicación moderada.
 *   · La pestaña enseñaba el motivo y el enlace, pero traía 50 filas y **no
 *     tenía filtro**: para buscar algo concreto había que ir a la otra pantalla,
 *     que no enseñaba el motivo.
 *
 * Aquí es un componente con las dos cosas, y las dos rutas lo montan. `#audit`
 * sigue existiendo porque es una dirección que la gente tiene guardada.
 *
 * ## Lo que cambia además
 *
 * - **El filtro no recarga en cada tecla**: espera a que se deje de escribir,
 *   como el resto de las listas del portal. El vanilla tenía un botón «Buscar»
 *   porque leía el campo del DOM al pulsarlo.
 * - **Ninguna de las dos tablas escapaba el nombre ni el motivo.**
 *   `${l.user_name}`, `${l.action_label}`, `${l.reason}` iban crudos en la
 *   plantilla; el motivo lo escribe un moderador a mano.
 * - **Un fallo se ve.** Era `catch { el.innerHTML = Utils.emptyState(…, 'Error
 *   cargando auditoría') }`: un estado vacío con cara de error, sin el motivo ni
 *   forma de reintentar.
 */
export function Auditoria({ suelta = false }: { suelta?: boolean }) {
  const [texto, setTexto] = useState('');
  const accion = useBusquedaDemorada(texto);
  const consulta = useAuditoria(accion, 100);

  const d = consulta.data;
  const filas = d?.items ?? [];

  return (
    <>
      {suelta && (
        <header className="mb-5">
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-shield-lock" aria-hidden="true" /> Auditoría del sistema
          </h1>
          <p className="mt-1 text-3 text-muted">
            Registro inmutable de todas las acciones.
          </p>
        </header>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <InputSuelto
          type="search"
          className="min-w-56 flex-1 sm:max-w-72"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Filtrar por acción: publicó, inició…"
          aria-label="Filtrar por acción"
        />
        <Button
          variant="secondary"
          size="sm"
          className="sm:ml-auto"
          onClick={() => Utils.downloadAuthed('/admin/audit-log/export', 'auditoria.xlsx')}
        >
          <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Exportar a Excel
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonFilas filas={8} columnas={5} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="el registro de auditoría"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !filas.length ? (
        <EstadoVacio
          icono="bi-shield-lock"
          titulo={accion ? 'Sin resultados' : 'Sin registros'}
          descripcion={accion
            ? `Ninguna acción coincide con «${accion}».`
            : 'Todavía no hay nada registrado.'}
        />
      ) : (
        <Card className="overflow-x-auto">
          <p className="border-b border-line bg-sunken p-3 text-2 text-muted">
            {d?.total ?? filas.length}{' '}
            {(d?.total ?? filas.length) === 1 ? 'registro encontrado' : 'registros encontrados'}
            {filas.length < (d?.total ?? 0) && ` · se muestran los ${filas.length} más recientes`}
          </p>
          <table className="w-full text-3">
            <caption className="sr-only">Registro de auditoría del portal</caption>
            <thead className="bg-sunken text-2">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Fecha y hora</th>
                <th scope="col" className="p-2 text-left font-semibold">Qué ocurrió</th>
                <th scope="col" className="p-2 text-left font-semibold">Persona</th>
                <th scope="col" className="p-2 text-left font-semibold">Motivo</th>
                <th scope="col" className="p-2 text-left font-semibold">Dirección IP</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((l, i) => (
                <tr key={`${l.created_at}-${i}`} className="border-t border-line">
                  <td className="whitespace-nowrap p-2 text-2 text-muted">
                    {Utils.fmtDatetime(l.created_at)}
                  </td>
                  <td className="p-2">
                    {l.summary || l.action_label || l.action}
                    {l.entity_label && (
                      <span className="block text-2 text-muted">{l.entity_label}</span>
                    )}
                    {l.entity_type === 'post' && l.entity_id != null && (
                      <a
                        href={`#novedades/publicacion/${l.entity_id}`}
                        className="text-2 text-brand-ink hover:underline focus-visible:focus-anillo"
                      >
                        <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Ver la
                        publicación
                      </a>
                    )}
                  </td>
                  <td className="p-2 font-semibold text-brand-ink">{l.user_name || 'Sistema'}</td>
                  <td className="p-2 text-2 text-muted">{l.reason || '—'}</td>
                  <td className="whitespace-nowrap p-2 font-mono text-2 text-muted">
                    {l.ip_address || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
